import { useEffect, useMemo, useRef } from "react";
import {
  useTransform,
  motion,
  MotionValue,
  useMotionValueEvent,
} from "motion/react";

export const SlideControls = ({
  total,
  value,
  onSelect,
}: {
  total: number;
  onSelect: (selected: number) => void;
  value: MotionValue<number>; // Must be between -1 and total
}) => {
  const controlContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!controlContainerRef.current!.style.display)
      controlContainerRef.current!.style.display = "none";
  }, []);

  useMotionValueEvent(value, "change", (value) => {
    const controlContainer = controlContainerRef.current;
    if (!controlContainer) return;
    if (value === -1 || value === total) {
      controlContainer.style.display = "none";
    } else {
      controlContainer.style.display = "block";
    }
  });

  const opacity = useTransform(value, [-1, 0, total - 1, total], [0, 1, 1, 0]);
  const markerY = useTransform(
    value,
    [-1, 0, total - 1, total],
    [`0%`, `0%`, `${100 * (total - 1)}%`, `${100 * (total - 1)}%`]
  );

  return (
    <motion.div
      ref={controlContainerRef}
      className="fixed top-1/2 -translate-y-1/2 right-8 z-10"
      style={{ opacity }}
    >
      <div className="relative flex flex-col p-1 rounded-xl bg-neutral-light/20 backdrop-blur-xl">
        {Array.from({ length: total }, (_, index) => (
          <SlideControlButton
            key={index}
            index={index}
            onClick={() => onSelect(index)}
            value={value}
          />
        ))}
        <motion.div
          className="absolute size-10 top-1 left-1 -z-10 bg-neutral-cream rounded-xl"
          style={{ y: markerY }}
        />
      </div>
    </motion.div>
  );
};

const SlideControlButton = ({
  index,
  onClick,
  value,
}: {
  index: number;
  onClick: () => void;
  value: MotionValue<number>;
}) => {
  const colorRange = useMemo(() => {
    const bodyStyles = window.getComputedStyle(document.body);
    return {
      dark: `rgb(${bodyStyles.getPropertyValue("--brand-black")} / 1)`,
      light: `rgb(${bodyStyles.getPropertyValue("--brand-cream")} / 1)`,
    };
  }, []);

  const color = useTransform(
    value,
    [index - 1, index, index + 1],
    [colorRange.light, colorRange.dark, colorRange.light]
  );

  return (
    <motion.button className="size-10" onClick={onClick} style={{ color }}>
      {index + 1}
    </motion.button>
  );
};
